import React, { useEffect, useState, useMemo } from "react";
import "./ProductLoanForm.css";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { fetchUserProfile } from "../redux/slices/branch/userProfileSlice";
import { toast } from "react-toastify";
import axios from "axios";
import { FaImage } from "react-icons/fa";

const initialFormState = {
  borrower: "",
  productName: "",
  productImage: null,
  imei1: "",
  imei2: "",
  serialNo: "",
  referId: "",
  referType: "",
  ref1Name: "",
  ref1Phone: "",
  ref1Address: "",
  ref1Image: null,
  vendorId: "",
  vendorName: "",
  displayProcFee: "",
  displayFileChg: "",
  displayIns: "",
  displayGst: "",
  baseAmount: "",
  totalCharges: "",
  billImage: null,
  loanDate: new Date().toISOString().split("T")[0],
  serviceType: "",
  schemeName: "",
  roiType: "",
  roi: "",
  loanAmount: "",
  penaltyCharge: "",
  gracePeriodDays: "",
  finePerPercentage: "",
  vendorPrice: "",
  downPayment: "",
  term: "",
  totalPayable: "",
  emiAmount: "",
  interestAmount: "",
};

const ProductLoanForm = () => {
  const dispatch = useDispatch();
  const { profile } = useSelector((state) => state.userProfile);

  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerPhoto, setCustomerPhoto] = useState(null);
  const [schemes, setSchemes] = useState([]);
  const [selectedScheme, setSelectedScheme] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [billImagePreview, setBillImagePreview] = useState(null);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState(initialFormState);

  useEffect(() => {
    dispatch(fetchUserProfile());
  }, [dispatch]);

  const getCalculatedCharge = (val, type, principal) => {
    const value = parseFloat(val) || 0;
    const t = type?.toLowerCase();
    return t === "percent" || t === "%" ? (principal * value) / 100 : value;
  };

  const calculatedLoanAmount = useMemo(() => {
    const price = parseFloat(formData.vendorPrice) || 0;
    const down = parseFloat(formData.downPayment) || 0;
    return price - down;
  }, [formData.vendorPrice, formData.downPayment]);

  const fetchCustomer = async () => {
    try {
      const res = await axios.get(`https://payyug.in/api/customers`, {
        params: { branchId: profile?.id || 8, page: 1, limit: 1000 },
      });
      setCustomers(res.data.data || []);
    } catch (err) {
      toast.error("Failed to fetch customers");
    }
  };

  const fetchSchemes = async () => {
    try {
      const res = await axios.get(
        `https://payyug.in/api/consumer-loan/scheme/list`,
        {
          params: { superAdminId: profile?.superAdminId, limit: 1000 },
        },
      );
      if (res.data.success) {
        setSchemes(res.data.data.data || []);
      }
    } catch (err) {
      console.error("Fetch Schemes Error:", err);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await axios.get(
        `https://payyug.in/api/product-loan/product/list`,
        {
          params: { superAdminId: profile?.superAdminId, limit: 1000 },
        },
      );
      if (res.data.success) {
        setProducts(res.data.data || []);
      }
    } catch (err) {
      console.error("Fetch Products Error:", err);
    }
  };

  const fetchVendors = async () => {
    try {
      const res = await axios.get(`https://payyug.in/api/vendor/master/list`, {
        params: { superAdminId: profile?.superAdminId, limit: 1000 },
      });
      if (res.data.success) {
        setVendors(res.data.data || []);
      }
    } catch (err) {
      console.error("Fetch Vendors Error:", err);
    }
  };

  useEffect(() => {
    if (profile) {
      fetchCustomer();
      fetchSchemes();
      fetchProducts();
      fetchVendors();
    }
  }, [profile]);

  useEffect(() => {
    if (selectedCustomer?.userId) {
      const getPhoto = async () => {
        setCustomerPhoto(null);
        try {
          const res = await fetch(
            `https://payyug.in/api/list-kyc?userId=${selectedCustomer.userId}&superAdminId=${profile?.superAdminId}&createdId=${profile?.id}`,
          );
          if (res.ok) {
            const data = await res.json();
            const photoDoc = data?.documents?.find(
              (doc) => doc?.docName?.toLowerCase() === "photo",
            );
            setCustomerPhoto(
              photoDoc?.docImage
                ? `https://payyug.in/uploads/${photoDoc.docImage}`
                : null,
            );
          }
        } catch (error) {
          console.error("Photo fetch failed", error);
        }
      };
      getPhoto();
    }
  }, [selectedCustomer, profile]);

  const availableSchemes = useMemo(() => {
    return schemes.filter((s) => s.serviceType === formData.serviceType);
  }, [formData.serviceType, schemes]);


  useEffect(() => {
    const fetchReferral = async () => {
      if (!formData.referId || !formData.referType) return;
      try {
        const apiUrl =
          formData.referType === "Employee"
            ? "https://payyug.in/api/hr-employees-filter"
            : "https://payyug.in/api/customers-get-userId";
        const res = await axios.get(apiUrl, {
          params: {
            userId: formData.referId,
            superAdminId: profile?.superAdminId,
          },
        });

        if (res.data.data) {
          const r = res.data.data;
          setFormData((prev) => ({
            ...prev,
            ref1Name:
              r.fullName || r.employeeName || r.customerName || r.name || "",
            ref1Phone: r.mobileNumber || r.phone || "",
            ref1Address: r.address || "",
          }));
        } else {
          setFormData((prev) => ({
            ...prev,
            ref1Name: "",
            ref1Phone: "",
            ref1Address: "",
          }));
        }
      } catch (err) {
        setFormData((prev) => ({
          ...prev,
          ref1Name: "",
          ref1Phone: "",
          ref1Address: "",
        }));
      }
    };
    fetchReferral();
  }, [formData.referId, formData.referType, profile]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, billImage: file }));
      setBillImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSchemeChange = (e) => {
    const schemeName = e.target.value;
    const selectedObj = availableSchemes.find(
      (s) => s.serviceName === schemeName,
    );
    if (selectedObj) {
      setSelectedScheme(selectedObj);
      setFormData((prev) => ({
        ...prev,
        schemeName: schemeName,
        roi: selectedObj.roi || "",
        roiType: selectedObj.roiType || "",
        penaltyCharge: selectedObj.penaltyValue || "",
        gracePeriodDays: selectedObj.gracePeriodDays || "",
        finePerPercentage: selectedObj.finePerPercentage || "",
      }));
    }
  };

  const handleProductChange = (e) => {
    const productName = e.target.value;
    const selectedObj = products.find((s) => s.productName === productName);
    if (selectedObj) {
      setSelectedProduct(selectedObj);
      setFormData((prev) => ({
        ...prev,
        productName: productName,
        productImage: selectedObj.image
          ? `https://payyug.in/uploads/${selectedObj.image}`
          : null,
      }));
    }
  };

  const handleVendorChange = (e) => {
    const vendorName = e.target.value;
    const selectedObj = vendors.find((s) => s.vendorName === vendorName);
    if (selectedObj) {
      setSelectedVendor(selectedObj);
      setFormData((prev) => ({
        ...prev,
        vendorId: selectedObj.id,
        vendorName: vendorName,
      }));
    }
  };

    useEffect(() => {
    const principal = parseFloat(calculatedLoanAmount) || 0;
    const rawTenure = parseFloat(formData.term) || 0;
    const yearlyRate = parseFloat(formData.roi) || 0;
    const gstPer = parseFloat(selectedScheme?.gstPercentage) || 0;

    const recoveryFreq =
      selectedScheme?.recoveryFrequency?.toLowerCase() || "monthly";
    const tenureType = selectedScheme?.tenureType?.toLowerCase() || "month";
    const roiType = selectedScheme?.roiType?.toLowerCase() || "reducing";
    const roiFreq = selectedScheme?.roiFrequency?.toLowerCase() || "monthly";

    if (principal > 0 && rawTenure > 0) {
      const processing = getCalculatedCharge(
        selectedScheme?.processingFeesValue,
        selectedScheme?.processingFeesType,
        principal
      );
      const fileChg = getCalculatedCharge(
        selectedScheme?.fileChargeValue,
        selectedScheme?.fileChargeType,
        principal
      );
      const insurance = getCalculatedCharge(
        selectedScheme?.insuranceValue,
        selectedScheme?.insuranceType,
        principal
      );

      const totalFees = processing + fileChg + insurance;
      const totalGstOnFees = (totalFees * gstPer) / 100;
      const finalChargesWithGst = totalFees + totalGstOnFees;

      const netPrincipal = principal + finalChargesWithGst;

      const factorMap = {
        daily: 365,
        day: 365,
        weekly: 52,
        week: 52,
        monthly: 12,
        month: 12,
        quarterly: 4,
        quarter: 4,
        "half-yearly": 2,
        "half-year": 2,
        yearly: 1,
        year: 1,
      };

      const recoveryFactor = factorMap[recoveryFreq] || 12;
      const roiFactor = factorMap[roiFreq] || 12;

      let totalInstalments = rawTenure;

      if (tenureType.includes("year")) {
        totalInstalments = rawTenure * recoveryFactor;
      } else if (tenureType.includes("month")) {
        if (recoveryFreq === "daily") totalInstalments = rawTenure * 30;
        else if (recoveryFreq === "weekly") totalInstalments = rawTenure * 4;
        else if (recoveryFreq === "monthly") totalInstalments = rawTenure;
      } else if (tenureType.includes("week")) {
        if (recoveryFreq === "daily") totalInstalments = rawTenure * 7;
        else totalInstalments = rawTenure;
      } else if (tenureType.includes("quarter")) {
        if (recoveryFreq === "monthly") totalInstalments = rawTenure * 3;
        else if (recoveryFreq === "weekly") totalInstalments = rawTenure * 13;
      }

      const periodicRate = yearlyRate / roiFactor / 100;
      let emi = 0;
      let totalInterest = 0;
      let totalPayable = 0;

      if (roiType === "flat" || roiType === "fixed") {
        const timeInYears = totalInstalments / recoveryFactor;
        totalInterest = netPrincipal * (yearlyRate / 100) * timeInYears;
        totalPayable = netPrincipal + totalInterest;
        emi = totalPayable / totalInstalments;
      } else {
        const adjustedRate =
          roiFactor === recoveryFactor
            ? periodicRate
            : yearlyRate / recoveryFactor / 100;
        if (adjustedRate > 0) {
          emi =
            (netPrincipal *
              adjustedRate *
              Math.pow(1 + adjustedRate, totalInstalments)) /
            (Math.pow(1 + adjustedRate, totalInstalments) - 1);
          totalPayable = emi * totalInstalments;
          totalInterest = totalPayable - netPrincipal;
        } else {
          emi = netPrincipal / totalInstalments;
          totalPayable = netPrincipal;
          totalInterest = 0;
        }
      }

      setFormData((prev) => ({
        ...prev,
        emiAmount: emi.toFixed(2),
        interestAmount: totalInterest.toFixed(2),
        totalPayable: totalPayable.toFixed(2),
        displayProcFee: processing.toFixed(2),
        displayFileChg: fileChg.toFixed(2),
        displayIns: insurance.toFixed(2),
        displayGst: totalGstOnFees.toFixed(2),
        baseAmount: principal.toFixed(2),
        totalCharges: finalChargesWithGst.toFixed(2),
        loanAmount: netPrincipal.toFixed(2),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        emiAmount: "",
        interestAmount: "",
        totalPayable: "",
        displayProcFee: "",
        displayFileChg: "",
        displayIns: "",
        displayGst: "",
        baseAmount: "",
        totalCharges: "",
        loanAmount: "",
      }));
    }
  }, [
    calculatedLoanAmount,
    formData.term,
    formData.roi,
    selectedScheme,
    formData.roiType,
  ]);

  const validate = () => {
    let newErrors = {};
    if (!formData.borrower) newErrors.borrower = "Please select a borrower";
    if (!formData.serviceType)
      newErrors.serviceType = "Please select a service";
    if (!formData.schemeName) newErrors.schemeName = "Please select a scheme";
    if (!formData.productName)
      newErrors.productName = "Please select a product";
    if (!formData.vendorName) newErrors.vendorName = "Please select a vendor";
    if (!formData.vendorPrice) newErrors.vendorPrice = "Required";
    if (!formData.downPayment) newErrors.downPayment = "Required";
    if (!formData.term) newErrors.term = "Required";

    const amount = parseFloat(calculatedLoanAmount) || 0;
    const minAmt = parseFloat(selectedScheme?.minAmount) || 0;
    const maxAmt = parseFloat(selectedScheme?.maxAmount) || Infinity;
    if (amount < minAmt)
      newErrors.baseAmount = `Minimum loan amount is ₹${minAmt}`;
    else if (amount > maxAmt)
      newErrors.baseAmount = `Maximum loan amount is ₹${maxAmt}`;

    const term = parseFloat(formData.term) || 0;
    const minTerm = parseFloat(selectedScheme?.minTenure) || 0;
    const maxTerm = parseFloat(selectedScheme?.maxTenure) || Infinity;
    if (term < minTerm || term > maxTerm)
      newErrors.term = `Tenure must be between ${minTerm} and ${maxTerm}`;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  if (!validate()) {
    toast.error("Please fill all required fields");
    return;
  }

  const principal = parseFloat(calculatedLoanAmount) || 0;


  const getPayloadValue = (val, type, displayVal) => {
    const t = type?.toLowerCase() || "";
    const isPercent = (t === "percent" || t === "%");
    const value = isPercent ? parseFloat(displayVal) : parseFloat(val);
    return isNaN(value) ? 0 : value;
  };

  const data = new FormData();

  const excludedKeys = [
    "displayProcFee", "displayFileChg", "displayIns", "displayGst", 
    "baseAmount", "totalCharges", "productImage"
  ];

  Object.keys(formData).forEach((key) => {
    if (!excludedKeys.includes(key) && formData[key] !== null) {
      data.append(key, formData[key]);
    }
  });

  data.append("processingFees", getPayloadValue(selectedScheme?.processingFeesValue, selectedScheme?.processingFeesType, formData.displayProcFee).toFixed(2));
  data.append("fileCharges", getPayloadValue(selectedScheme?.fileChargeValue, selectedScheme?.fileChargeType, formData.displayFileChg).toFixed(2));
  data.append("insuranceAmount", getPayloadValue(selectedScheme?.insuranceValue, selectedScheme?.insuranceType, formData.displayIns).toFixed(2));
  data.append("gstAmount", parseFloat(formData.displayGst || 0).toFixed(2));

  data.append("superAdminId", profile?.superAdminId || "");
  data.append("branchId", profile?.id || "");
  data.append("borrowerId", selectedCustomer?.id || "");
  data.append("productId", selectedProduct?.id || "");
  data.append("modelId", selectedProduct?.model?.id || "");
  data.append("brandId", selectedProduct?.brand?.id || "");
  data.append("variantId", selectedProduct?.variant?.id || "");
  data.append("categoryId", selectedProduct?.category?.id || "");
  data.append("schemeId",selectedScheme?.id||"")
  data.append("prefix",selectedScheme?.prefix||"")

  try {
    const response = await axios.post("https://payyug.in/api/product-loan/apply", data);
    toast.success(response?.data?.message || "Application Submitted!");

    setFormData(initialFormState);
    setSelectedCustomer(null);
    setCustomerPhoto(null);
    setSelectedScheme(null);
    setSelectedProduct(null);
    setSelectedVendor(null);
    setBillImagePreview(null);
    setErrors({});
  } catch (err) {
    console.error("Submission Error:", err);
    toast.error(err?.response?.data?.message || "Submission Failed");
  }
};
  return (
    <div className="product-loan-container">
      <h2 className="product-loan-heading">Product Loan Form</h2>
      <form onSubmit={handleSubmit} className="product-loan-form-wrapper">
        <fieldset className="product-loan-form-section">
          <legend>Borrower Details</legend>
          <div className="product-loan-form-row">
            <div className="product-loan-form-col">
              <label>
                Borrower <span>*</span>
              </label>
              <Select
                className="account-select-container"
                classNamePrefix="account-select"
                placeholder="Search Customer"
                value={
                  selectedCustomer
                    ? {
                        value: selectedCustomer.id,
                        label: `${selectedCustomer.userId} | ${selectedCustomer.fullName}`,
                      }
                    : null
                }
                options={customers.map((cus) => ({
                  value: cus.id,
                  label: `${cus.userId} | ${cus.fullName}`,
                  customerDetail: cus,
                }))}
                onChange={(s) => {
                  setSelectedCustomer(s?.customerDetail);
                  setFormData((prev) => ({
                    ...prev,
                    borrower: s?.value || "",
                  }));
                }}
                isSearchable
              />
              {errors.borrower && (
                <p className="error-text">{errors.borrower}</p>
              )}
            </div>
          </div>
          <div className="dynamic-borrower-info">
            <div className="dynamic-borrower-details">
              <p>
                Name: <span>{selectedCustomer?.fullName || "N/A"}</span>
              </p>
              <p>
                Guardian: <span>{selectedCustomer?.guardianName || "N/A"}</span>
              </p>
              <p>
                DOB:{" "}
                <span>
                  {selectedCustomer?.dateOfBirth
                    ? new Date(selectedCustomer.dateOfBirth).toLocaleDateString(
                        "en-GB",
                      )
                    : "N/A"}
                </span>
              </p>
              <p>
                Gender: <span>{selectedCustomer?.gender || "N/A"}</span>
              </p>
              <p>
                Phone: <span>{selectedCustomer?.mobileNumber || "N/A"}</span>
              </p>
              <p>
                Address: <span>{selectedCustomer?.address || "N/A"}</span>
              </p>
            </div>
            <div className="dynamic-borrower-profile">
              <div className="img-preview">
                {customerPhoto ? (
                  <img
                    src={customerPhoto}
                    alt="Profile"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                ) : (
                  "Profile Image"
                )}
              </div>
            </div>
          </div>
        </fieldset>

        <fieldset className="product-loan-form-section">
          <legend>Product Info</legend>
          <div className="product-loan-product-info">
            <div className="product-loan-product-info-grid">
              <div className="product-loan-form-col">
                <label>
                  Product <span>*</span>
                </label>
                <select
                  name="productName"
                  value={formData.productName}
                  onChange={handleProductChange}
                >
                  <option value="">Select Product</option>
                  {products.map((s) => (
                    <option key={s.id} value={s.productName}>
                      {s.productName}
                    </option>
                  ))}
                </select>
                {errors.productName && (
                  <p className="error-text">{errors.productName}</p>
                )}
              </div>
              <div className="product-loan-form-col">
                <label>Category</label>
                <input
                  type="text"
                  value={selectedProduct?.category?.name || ""}
                  readOnly
                  className="readonly-input"
                />
              </div>
              <div className="product-loan-form-col">
                <label>Brand</label>
                <input
                  type="text"
                  value={selectedProduct?.brand?.name || ""}
                  readOnly
                  className="readonly-input"
                />
              </div>
              <div className="product-loan-form-col">
                <label>Model</label>
                <input
                  type="text"
                  value={selectedProduct?.model?.name || ""}
                  readOnly
                  className="readonly-input"
                />
              </div>
              <div className="product-loan-form-col">
                <label>Varient</label>
                <input
                  type="text"
                  value={selectedProduct?.variant?.name || ""}
                  readOnly
                  className="readonly-input"
                />
              </div>
              <div className="product-loan-form-col">
                <label>IEMI 1</label>
                <input
                  name="imei1"
                  type="text"
                  value={formData.imei1 || ""}
                  onChange={handleChange}
                />
              </div>
              <div className="product-loan-form-col">
                <label>IEMI 2</label>
                <input
                  name="imei2"
                  type="text"
                  value={formData.imei2 || ""}
                  onChange={handleChange}
                />
              </div>
              <div className="product-loan-form-col">
                <label>Serial No</label>
                <input
                  name="serialNo"
                  type="text"
                  value={formData.serialNo || ""}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="product-image-side">
              <div className="img-preview">
                {formData.productImage ? (
                  <img
                    src={formData.productImage}
                    alt="product"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                ) : (
                  "Product Image"
                )}
              </div>
            </div>
          </div>
        </fieldset>

        <fieldset className="product-loan-form-section">
          <legend>Vendor Details</legend>
          <div className="product-loan-form-row">
            <div className="product-loan-form-col">
              <label>
                Vendor <span>*</span>
              </label>
              <select
                name="vendorName"
                value={formData.vendorName}
                onChange={handleVendorChange}
              >
                <option value="">Select Vendor</option>
                {vendors.map((s) => (
                  <option key={s.id} value={s.vendorName}>
                    {s.vendorName} - {s.vendorCode}
                  </option>
                ))}
              </select>
              {errors.vendorName && (
                <p className="error-text">{errors.vendorName}</p>
              )}
            </div>
            <div className="product-loan-form-col">
              <label>Vendor Code</label>
              <input
                type="text"
                value={selectedVendor?.vendorCode || ""}
                readOnly
                className="readonly-input"
              />
            </div>
            <div className="product-loan-form-col">
              <label>Mobile</label>
              <input
                type="text"
                value={selectedVendor?.mobileNo || ""}
                readOnly
                className="readonly-input"
              />
            </div>
            <div className="product-loan-form-col">
              <label>Email</label>
              <input
                type="text"
                value={selectedVendor?.email || ""}
                readOnly
                className="readonly-input"
              />
            </div>
            <div className="product-loan-form-col">
              <label>Address</label>
              <input
                type="text"
                value={selectedVendor?.vendorAddress || ""}
                readOnly
                className="readonly-input"
              />
            </div>
          </div>
        </fieldset>

        <fieldset className="product-loan-form-section">
          <legend>Scheme Details</legend>
          <div className="product-loan-form-row">
            <div className="product-loan-form-col">
              <label>
                Service Type <span>*</span>
              </label>
              <select
                name="serviceType"
                value={formData.serviceType}
                onChange={handleChange}
              >
                <option value="">Select Type</option>
                {[...new Set(schemes.map((s) => s.serviceType))].map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
              {errors.serviceType && (
                <p className="error-text">{errors.serviceType}</p>
              )}
            </div>
            <div className="product-loan-form-col">
              <label>
                Scheme Name <span>*</span>
              </label>
              <select
                name="schemeName"
                value={formData.schemeName}
                onChange={handleSchemeChange}
                disabled={!formData.serviceType}
              >
                <option value="">Select Scheme</option>
                {availableSchemes.map((s) => (
                  <option key={s.id} value={s.serviceName}>
                    {s.serviceName}
                  </option>
                ))}
              </select>
              {errors.schemeName && (
                <p className="error-text">{errors.schemeName}</p>
              )}
            </div>
            <div className="product-loan-form-col">
              <label>Minimum Amount</label>
              <input
                type="text"
                value={selectedScheme?.minAmount || ""}
                readOnly
                className="readonly-input"
              />
            </div>
            <div className="product-loan-form-col">
              <label>Maximum Amount</label>
              <input
                type="text"
                value={selectedScheme?.maxAmount || ""}
                readOnly
                className="readonly-input"
              />
            </div>
            <div className="product-loan-form-col">
              <label>Min Tenure</label>
              <input
                type="text"
                value={selectedScheme?.minTenure || ""}
                readOnly
                className="readonly-input"
              />
            </div>
            <div className="product-loan-form-col">
              <label>Max Tenure</label>
              <input
                type="text"
                value={selectedScheme?.maxTenure || ""}
                readOnly
                className="readonly-input"
              />
            </div>
            <div className="product-loan-form-col">
              <label>Tenure Type</label>
              <input
                type="text"
                value={selectedScheme?.tenureType || ""}
                readOnly
                className="readonly-input"
              />
            </div>
            <div className="product-loan-form-col">
              <label>Roi(%)</label>
              <input
                type="text"
                value={formData.roi || ""}
                readOnly
                className="readonly-input"
              />
            </div>
            <div className="product-loan-form-col">
              <label>Roi Type</label>
              <input
                type="text"
                value={selectedScheme?.roiType || ""}
                readOnly
                className="readonly-input"
              />
            </div>
            <div className="product-loan-form-col">
              <label>Roi Frequency</label>
              <input
                type="text"
                value={selectedScheme?.roiFrequency || ""}
                readOnly
                className="readonly-input"
              />
            </div>
            <div className="product-loan-form-col">
              <label>Recovery Frequency</label>
              <input
                type="text"
                value={selectedScheme?.recoveryFrequency || ""}
                readOnly
                className="readonly-input"
              />
            </div>
            <div className="product-loan-form-col">
              <label>
                Processing Fees{" "}
                {selectedScheme?.processingFeesType
                  ? `(${selectedScheme.processingFeesType.toLowerCase() === "percent" ? "%" : "Rs."})`
                  : ""}
              </label>
              <input
                type="text"
                value={
                  selectedScheme?.processingFeesType?.toLowerCase() ===
                    "percent" ||
                  selectedScheme?.processingFeesType?.toLowerCase() === "%"
                    ? `${selectedScheme?.processingFeesValue}% (₹${formData.displayProcFee || 0})`
                    : `₹${selectedScheme?.processingFeesValue || 0}`
                }
                readOnly
                className="readonly-input"
              />
            </div>
            <div className="product-loan-form-col">
              <label>
                Penalty Charge{" "}
                {selectedScheme?.penaltyType
                  ? `(${selectedScheme.penaltyType.toLowerCase() === "percent" ? "%" : "Rs."})`
                  : ""}
              </label>
              <input
                type="text"
                value={formData.penaltyCharge || ""}
                readOnly
                placeholder="0.00"
                className="readonly-input"
              />
            </div>
            <div className="product-loan-form-col">
              <label>
                File Charges{" "}
                {selectedScheme?.fileChargeType
                  ? `(${selectedScheme.fileChargeType.toLowerCase() === "percent" ? "%" : "Rs."})`
                  : ""}
              </label>
              <input
                type="text"
                value={
                  selectedScheme?.fileChargeType?.toLowerCase() === "percent" ||
                  selectedScheme?.fileChargeType?.toLowerCase() === "%"
                    ? `${selectedScheme?.fileChargeValue}% (₹${formData.displayFileChg || 0})`
                    : `₹${selectedScheme?.fileChargeValue || 0}`
                }
                readOnly
                className="readonly-input"
              />
            </div>
            <div className="product-loan-form-col">
              <label>
                Insurance{" "}
                {selectedScheme?.insuranceType
                  ? `(${selectedScheme.insuranceType.toLowerCase() === "percent" ? "%" : "Rs."})`
                  : ""}
              </label>
              <input
                type="text"
                value={
                  selectedScheme?.insuranceType?.toLowerCase() === "percent" ||
                  selectedScheme?.insuranceType?.toLowerCase() === "%"
                    ? `${selectedScheme?.insuranceValue}% (₹${formData.displayIns || 0})`
                    : `₹${selectedScheme?.insuranceValue || 0}`
                }
                readOnly
                className="readonly-input"
              />
            </div>
            <div className="product-loan-form-col">
              <label>Grace Period Days</label>
              <input
                type="text"
                value={selectedScheme?.gracePeriodDays || ""}
                readOnly
                className="readonly-input"
              />
            </div>
            <div className="product-loan-form-col">
              <label>GST (%) on (processing+file+insurance)</label>
              <input
                type="text"
                value={
                  selectedScheme?.gstPercentage
                    ? `${selectedScheme?.gstPercentage}% (₹${formData.displayGst || 0})`
                    : ""
                }
                readOnly
                className="readonly-input"
              />
            </div>
            <div className="product-loan-form-col">
              <label>Fine per (%)</label>
              <input
                type="text"
                value={selectedScheme?.finePerPercentage || ""}
                readOnly
                className="readonly-input"
              />
            </div>
            <div className="product-loan-form-col">
              <label>
                Vendor Price <span>*</span>
              </label>
              <input
                name="vendorPrice"
                type="number"
                className={errors.vendorPrice ? "error-input" : ""}
                value={formData.vendorPrice || ""}
                onChange={handleChange}
              />
              {errors.vendorPrice && (
                <p className="error-text">{errors.vendorPrice}</p>
              )}
            </div>
            <div className="product-loan-form-col">
              <label>
                Down Payment <span>*</span>
              </label>
              <input
                name="downPayment"
                type="number"
                className={errors.downPayment ? "error-input" : ""}
                value={formData.downPayment || ""}
                onChange={handleChange}
              />
              {errors.downPayment && (
                <p className="error-text">{errors.downPayment}</p>
              )}
            </div>
            <div className="product-loan-form-col">
              <label>
                Loan Tenure <span>*</span>
              </label>
              <input
                name="term"
                type="number"
                className={errors.term ? "error-input" : ""}
                value={formData.term}
                onChange={handleChange}
              />
              {errors.term && <p className="error-text">{errors.term}</p>}
            </div>
            <div className="product-loan-form-col">
              <label>Base Amount (Vendor Price - Down Payment)</label>
              <input
                type="text"
                value={calculatedLoanAmount}
                readOnly
                className="readonly-input"
              />
            </div>
            <div className="product-loan-form-col">
              <label>Total Charges (Fees + GST)</label>
              <input
                type="text"
                value={formData.totalCharges}
                readOnly
                className="readonly-input"
              />
            </div>
            <div className="product-loan-form-col">
              <label>Loan Amount (Net Principal)</label>
              <input
                type="text"
                value={formData.loanAmount}
                readOnly
                className="readonly-input"
              />
            </div>
            <div className="product-loan-form-col">
              <label>Emi Amount</label>
              <input
                type="text"
                value={formData.emiAmount}
                readOnly
                className="readonly-input"
              />
            </div>
            <div className="product-loan-form-col">
              <label>Total Interest</label>
              <input
                type="text"
                value={formData.interestAmount}
                readOnly
                className="readonly-input"
              />
            </div>
            <div className="product-loan-form-col">
              <label>Total Payable Amount</label>
              <input
                type="text"
                value={formData.totalPayable}
                readOnly
                className="readonly-input"
              />
            </div>
            <div className="product-loan-form-col">
              <label>Loan Date</label>
              <input
                type="date"
                name="loanDate"
                value={formData.loanDate}
                onChange={handleChange}
              />
            </div>
          </div>
        </fieldset>

        <fieldset className="product-loan-form-section">
          <legend>Document Details</legend>
          <div className="product-loan-document">
            <div className="product-loan-document-card">
              <p className="document-heading">Bill Image</p>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
              />
              {billImagePreview ? (
                <div className="document-img-preview">
                  <img src={billImagePreview} alt="bill preview" />
                </div>
              ) : (
                <div className="image-placeholder">
                  <FaImage size={30} />
                </div>
              )}
            </div>
          </div>
        </fieldset>

        <fieldset className="product-loan-form-section">
          <legend>Referral Details</legend>
          <div className="product-loan-form-row">
            <div className="product-loan-form-col">
              <label>Referral Person</label>
              <select
                value={formData.referType}
                onChange={(e) =>
                  setFormData({ ...formData, referType: e.target.value })
                }
              >
                <option value="">Select Referral Type</option>
                <option value="Customer">Customer</option>
                <option value="Employee">Employee</option>
              </select>
            </div>
            <div className="product-loan-form-col">
              <label>Referral Id</label>
              <input
                type="text"
                placeholder="Enter Referral ID"
                value={formData.referId}
                onChange={(e) =>
                  setFormData({ ...formData, referId: e.target.value })
                }
              />
            </div>
            <div className="product-loan-form-col">
              <label>Referral Name</label>
              <input
                type="text"
                value={formData.ref1Name}
                readOnly
                className="readonly-input"
              />
            </div>
            <div className="product-loan-form-col">
              <label>Referral Mobile</label>
              <input
                type="text"
                value={formData.ref1Phone}
                readOnly
                className="readonly-input"
              />
            </div>
            <div className="product-loan-form-col">
              <label>Referral Address</label>
              <input
                type="text"
                value={formData.ref1Address}
                readOnly
                className="readonly-input"
              />
            </div>
          </div>
        </fieldset>

        <div className="form-actions">
          <button type="submit" className="save-btn">
            Submit
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProductLoanForm;
